import React, { useState, useEffect } from 'react';
import { apiClient } from '../api/client';
import { useAuthStore } from '../stores/authStore';

interface ShiftData {
  id: string;
  date: string;
  pattern: string;
  status: string;
  hoursWorked?: number;
}

interface ComplianceTask {
  id: string;
  title: string;
  dueDate: string;
  status: string;
  priority: string;
}

interface LeaveBalance {
  leaveType: string;
  available: number;
  used: number;
  pending: number;
}

export default function MobileStaffApp() {
  // Identify from the signed-in session — this was blank state, so nothing loaded
  const { staff } = useAuthStore();
  const staffId = staff?.id || '';
  const branchId = staff?.branchId || '';
  const [activeTab, setActiveTab] = useState('shifts');
  const [shifts, setShifts] = useState<ShiftData[]>([]);
  const [complianceTasks, setComplianceTasks] = useState<ComplianceTask[]>([]);
  const [leaveBalance, setLeaveBalance] = useState<LeaveBalance[]>([]);
  const [loading, setLoading] = useState(false);
  const [showLeaveForm, setShowLeaveForm] = useState(false);
  const [leaveType, setLeaveType] = useState('annual');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [leaveReason, setLeaveReason] = useState('');

  useEffect(() => {
    if (staffId) {
      fetchStaffData();
    }
  }, [staffId, activeTab]);

  const fetchStaffData = async () => {
    try {
      setLoading(true);
      if (activeTab === 'shifts') {
        const response = await apiClient.getStaffRota(staffId);
        setShifts(response.data);
      } else if (activeTab === 'compliance') {
        // signature is (branchId, status?, staffId?) — the staff id is third
        const response = await apiClient.getComplianceTasks(branchId, undefined, staffId);
        setComplianceTasks(Array.isArray(response.data) ? response.data : response.data?.tasks || []);
      } else if (activeTab === 'leave') {
        const response = await apiClient.getLeaveBalance(staffId);
        setLeaveBalance(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch staff data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestLeave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiClient.requestLeave(staffId, leaveType, startDate, endDate, leaveReason);
      setShowLeaveForm(false);
      setStartDate('');
      setEndDate('');
      setLeaveReason('');
      fetchStaffData();
    } catch (error) {
      console.error('Failed to request leave:', error);
    }
  };

  const getShiftStatus = (status: string) => {
    const icons: Record<string, string> = {
      assigned: '📋',
      approved: '✅',
      worked: '🏁',
      absent: '❌',
      cancelled: '⛔',
    };
    return icons[status] || '❓';
  };

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      high: '#dc3545',
      medium: '#ffc107',
      low: '#28a745',
      critical: '#721c24',
    };
    return colors[priority] || '#6c757d';
  };

  const formatDate = (date: string) => new Date(date).toLocaleDateString('en-ZA', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  return (
    <div style={{ maxWidth: '500px', margin: '0 auto', padding: '10px', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <div style={{ backgroundColor: '#B45309', color: '#fff', padding: '15px', borderRadius: '8px', marginBottom: '15px', textAlign: 'center' }}>
        <h1 style={{ margin: '0 0 4px 0', fontSize: '24px' }}>MepCo Staff App</h1>
        <p style={{ margin: 0, fontSize: '14px', opacity: 0.9 }}>
          {staff?.name}{staff?.branchName ? ` · ${staff.branchName}` : ''}
        </p>
      </div>

      {staffId && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '15px' }}>
            {['shifts', 'leave', 'compliance'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: '12px',
                  backgroundColor: activeTab === tab ? '#007bff' : '#f0f0f0',
                  color: activeTab === tab ? '#fff' : '#000',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  textTransform: 'capitalize',
                  transition: 'all 0.3s ease',
                }}
              >
                {tab === 'shifts' && '📅'}
                {tab === 'leave' && '🏖️'}
                {tab === 'compliance' && '✓'}
                <br />
                {tab}
              </button>
            ))}
          </div>

          {loading && <p style={{ textAlign: 'center', padding: '20px' }}>Loading...</p>}

          {!loading && activeTab === 'shifts' && (
            <div>
              <h2 style={{ fontSize: '18px', marginBottom: '15px' }}>📅 Your Shifts</h2>
              {shifts.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#666' }}>No shifts assigned yet</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {shifts.map((shift) => (
                    <div
                      key={shift.id}
                      style={{
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        backgroundColor: '#f9f9f9',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                            {getShiftStatus(shift.status)} {shift.pattern}
                          </div>
                          <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                            {formatDate(shift.date)}
                          </div>
                        </div>
                        <div style={{ fontSize: '12px', textAlign: 'right' }}>
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '4px 8px',
                              backgroundColor: '#e7f3ff',
                              borderRadius: '4px',
                              textTransform: 'capitalize',
                            }}
                          >
                            {shift.status}
                          </span>
                          {shift.hoursWorked && <div style={{ marginTop: '4px' }}>{shift.hoursWorked}h worked</div>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {!loading && activeTab === 'leave' && (
            <div>
              <h2 style={{ fontSize: '18px', marginBottom: '15px' }}>🏖️ Leave Balance</h2>

              {leaveBalance.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#666' }}>No leave data available</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '15px' }}>
                  {leaveBalance.map((balance) => (
                    <div
                      key={balance.leaveType}
                      style={{
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        backgroundColor: '#f9f9f9',
                      }}
                    >
                      <div style={{ fontWeight: 'bold', marginBottom: '8px', textTransform: 'capitalize' }}>
                        {balance.leaveType} Leave
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '4px' }}>
                        <span>Available:</span>
                        <span style={{ fontWeight: 'bold', color: '#28a745' }}>{balance.available} days</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '4px' }}>
                        <span>Used:</span>
                        <span>{balance.used} days</span>
                      </div>
                      {balance.pending > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#ff9800' }}>
                          <span>Pending Approval:</span>
                          <span>{balance.pending} days</span>
                        </div>
                      )}
                      <div
                        style={{
                          marginTop: '8px',
                          height: '8px',
                          backgroundColor: '#e0e0e0',
                          borderRadius: '4px',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            backgroundColor: '#28a745',
                            width: `${(balance.available / (balance.available + balance.used)) * 100}%`,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={() => setShowLeaveForm(!showLeaveForm)}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#B45309',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                }}
              >
                {showLeaveForm ? '✕ Close Form' : '➕ Request Leave'}
              </button>

              {showLeaveForm && (
                <form onSubmit={handleRequestLeave} style={{ marginTop: '15px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <select
                      value={leaveType}
                      onChange={(e) => setLeaveType(e.target.value)}
                      style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
                    >
                      <option value="annual">Annual Leave</option>
                      <option value="sick">Sick Leave</option>
                      <option value="compassionate">Compassionate Leave</option>
                      <option value="unpaid">Unpaid Leave</option>
                    </select>

                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
                      required
                    />

                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
                      required
                    />

                    <textarea
                      placeholder="Reason for leave..."
                      value={leaveReason}
                      onChange={(e) => setLeaveReason(e.target.value)}
                      style={{
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        minHeight: '80px',
                        fontFamily: 'inherit',
                      }}
                      required
                    />

                    <button
                      type="submit"
                      style={{
                        padding: '10px',
                        backgroundColor: '#28a745',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                      }}
                    >
                      Submit Request
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {!loading && activeTab === 'compliance' && (
            <div>
              <h2 style={{ fontSize: '18px', marginBottom: '15px' }}>✓ Compliance Tasks</h2>
              {complianceTasks.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#666' }}>No compliance tasks assigned</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {complianceTasks.map((task) => (
                    <div
                      key={task.id}
                      style={{
                        padding: '12px',
                        border: `2px solid ${getPriorityColor(task.priority)}`,
                        borderRadius: '4px',
                        backgroundColor: '#f9f9f9',
                      }}
                    >
                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{task.title}</div>
                      <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                        Due: {formatDate(task.dueDate)}
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <span
                          style={{
                            padding: '2px 6px',
                            backgroundColor: '#e7f3ff',
                            borderRadius: '3px',
                            fontSize: '12px',
                            textTransform: 'capitalize',
                          }}
                        >
                          {task.status}
                        </span>
                        <span
                          style={{
                            padding: '2px 6px',
                            backgroundColor: getPriorityColor(task.priority),
                            color: '#fff',
                            borderRadius: '3px',
                            fontSize: '12px',
                            textTransform: 'capitalize',
                          }}
                        >
                          {task.priority}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div
        style={{
          marginTop: '30px',
          paddingTop: '15px',
          borderTop: '1px solid #ddd',
          fontSize: '12px',
          textAlign: 'center',
          color: '#666',
        }}
      >
        <p>MEPCO Point of Sale System • Staff Portal v1.0</p>
        <p>Dough & Soul / Breadbin Outlets</p>
      </div>
    </div>
  );
}
